import React from 'react';
import { AccountInputsState } from '../../hooks/AccountInputs';
import ConnectedCaptcha from '../Captcha';
import ConnectedToSInfo from '../ToSInfo';
import { Button } from '@sendgrid/ui-components/button';
import PasswordInput from '../PasswordInput';
import { TextInput } from '@sendgrid/ui-components/text-input';
import Checkbox from '@sendgrid/ui-components/checkbox';
import GetStartedModal from '../GetStartedModal';
import { WriteSelectors } from './qa-hooks';
const Styles = require('./index.module.scss');

interface AccountInputsV2Props {
  onToggleUseEmailAddress: () => void;
  onChangeEmail: (e: React.FormEvent<HTMLInputElement>) => void;
  onBlurEmail: () => void;
  onChangeUsername: (e: React.FormEvent<HTMLInputElement>) => void;
  onBlurUsername: () => void;
  onChangePassword: (e: React.FormEvent<HTMLInputElement>) => void;
  onBlurPassword: () => void;
  onPasswordError: (error: string) => void;
  accountInputsState: AccountInputsState;
  history: any;
  formInputs: any;
  createUser: any;
  packages: any;
}

/**
 * This component is an optimized form derived from https://jira.sendgrid.net/browse/CL-1768
 *  - since we are not yet moving the AWS reseller form over to this one, we are keeping the
 *    old AccountInputsV1 component and state manager (actions|reducers)/formInputs for the time being
 * We will likely convert the AWS flow to this component at some point in the future
 *  - at which point the state managers should be consolidated
 */
const AccountInputsV2: React.FC<AccountInputsV2Props> = ({
  history,
  accountInputsState, // <-- this is the new state manager using hooks
  onToggleUseEmailAddress, // <-- new
  onChangeEmail, // <-- new
  onBlurEmail, // <-- new
  onChangeUsername, // <-- new
  onBlurUsername, // <-- new
  onChangePassword, // <-- new
  onBlurPassword, // <-- new
  formInputs, // <-- this is the legacy state manager for the first AccountInputsV1 using redux
  createUser, // <-- legacy
  packages, // <-- legacy
  onPasswordError
}) => {
  /**
   * since both AccountInputsV1 and AccountInputsV2 use the same Captcha and TOS, we are leaving them as is
   *  - whenever the AWS move to AccountInputsV2 occurs, this state and their respective components (below)
   *    should be updated to consolidate state
   */
  const { captcha_isValid, tos, recaptcha_token } = formInputs;

  const {
    useEmailAddress,
    emailValue,
    emailInfo,
    emailIsValid,
    usernameValue,
    usernameInfo,
    usernameIsValid,
    passwordValue,
    passwordInfo,
    passwordIsValid
  } = accountInputsState;

  const onSubmit = () => {
    const postBody = {
      username: useEmailAddress ? emailValue : usernameValue,
      password: passwordValue,
      email: emailValue,
      recaptcha_token: recaptcha_token
    };

    createUser(
      postBody,
      packages.activePackage,
      packages.activeIsFree,
      history,
      useEmailAddress,
      onPasswordError
    );
  };

  const hasEmptyFields = Boolean(
    emailValue == '' ||
      (useEmailAddress ? false : usernameValue === '') ||
      passwordValue === ''
  );
  const hasInlineErrors = Boolean(
    !emailIsValid ||
      (useEmailAddress ? false : !usernameIsValid) ||
      !passwordIsValid
  );
  const isInvalidForm =
    hasEmptyFields || hasInlineErrors || !captcha_isValid || tos != 1;

  return (
    <>
      <GetStartedModal />
      <section className="form-container">
        <div className={Styles['account-inputs-v2-email-field']}>
          <TextInput
            type="email"
            label="Email Address"
            isRequired
            isValid={emailIsValid}
            id="email"
            info={emailInfo}
            onChange={onChangeEmail}
            onBlur={onBlurEmail}
            value={emailValue}
            data-role="email-field"
            {...WriteSelectors.emailInput}
          />
        </div>

        <Checkbox
          checked={useEmailAddress}
          onChange={onToggleUseEmailAddress}
          id="use-email-address-checkbox"
          {...WriteSelectors.useEmailCheckbox}
        >
          Use email address as username
        </Checkbox>

        {!useEmailAddress && (
          <TextInput
            type="text"
            label="Username"
            isRequired
            isValid={usernameIsValid}
            id="username"
            info={usernameInfo}
            onChange={onChangeUsername}
            onBlur={onBlurUsername}
            value={usernameValue}
            data-role="username-field"
            {...WriteSelectors.usernameInput}
          />
        )}

        <PasswordInput
          value={passwordValue}
          errorInfo={passwordInfo}
          onChange={onChangePassword}
          onBlur={onBlurPassword}
          passwordLengthOnlyValidation={true}
          data-role="password-field"
          {...WriteSelectors.passwordInput}
        />

        <ConnectedCaptcha />
        <ConnectedToSInfo />
        <Button
          className={Styles['account-inputs-v2-create-account-button']}
          type="primary"
          onClick={onSubmit}
          disabled={isInvalidForm}
          {...WriteSelectors.submitButton}
        >
          {packages.activeIsFree ? 'Create Account' : 'Continue'}
        </Button>
      </section>
    </>
  );
};

export default AccountInputsV2;
